// ============================================================
// Selbstvermessung fuer die Lade-Skelette.
//
// Das Skelett soll exakt so aussehen wie das, was danach kommt — sonst
// springt die Seite beim Umschalten. Feste Zahlen im Code veralten aber bei
// jeder UI-Aenderung still (genau das ist zweimal passiert). Deshalb misst
// die FERTIGE Seite sich selbst und legt ihre Masse ab; das naechste Laden
// liest sie. Ohne Messung gelten die einkodierten Rueckfallwerte.
//
// Der Schluessel traegt alles, was die Geometrie aendert: Breitenband,
// aktiver Filter, Projektschiene, Projekt (Namenslaengen, Chip-Anzahl).
//
// GEMESSEN WIRD SEIT DIESER RUNDE NICHT NUR DIE HOEHE.
// Die alte Fassung kannte sechs Hoehen — rail/kopf/suche/filter/hero/tabs —
// und traf sie auf 0–1 px. Nur: das sind genau die Bloecke OBERHALB des
// Reiterinhalts, und der macht rund 60 % der Seite aus. Die Silhouette malte
// dort EINE Riesenkarte, wo drei Rasterkacheln stehen; gemessen 43 % Deckung,
// 31 % der Skelettflaeche stand, wo nie etwas kam. Wieviele Diagrammkacheln
// es gibt, wie breit sie sind und wie lang die Beschriftungen sind, kann eine
// Silhouette beim Zeichnen prinzipiell nicht wissen — beim naechsten Mal
// koennte sie es aber wissen. Also merkt sie es sich hier.
//
// Praefix bleibt skm4: die neuen Felder kommen HINZU und sind alle optional.
// Ein alter Eintrag traegt weiterhin sechs richtige Hoehen — er luegt nicht,
// er weiss nur weniger. Ein Praefixwechsel haette dagegen auch die Hoehen
// weggeworfen, die schon stimmten.
// ============================================================
import { load, save } from "./storage";

/** Kachel im Diagrammraster: [Spannweite 1..3, Hoehe px, Hoehe der Hinweiszeile px]. */
export type KachelMass = [number, number, number];

/** Was gemessen wird. Die sechs Hoehen sind Pflicht (ohne sie ist die Messung
 *  wertlos), alles Weitere ist optional — es kam spaeter dazu und fehlt in
 *  aelteren Eintraegen. */
export interface SkelettMass {
  rail: number;
  kopf: number;
  suche: number;
  filter: number;
  hero: number;
  tabs: number;
  /** Textbreite der Ueberschrift (Projektname) */
  titel?: number;
  /** Breiten der Kopfknoepfe, ganze Schaltflaeche */
  kopfBtn?: number[];
  /** Breiten der Filterchips, Gruppe 1 (Auswahlen) und Gruppe 2 (Spalte/Lesezeichen) */
  chips?: number[];
  chips2?: number[];
  /** Textbreiten in den Kennzahlkacheln — Laenge = Zahl der Kacheln */
  heroLab?: number[];
  heroVal?: number[];
  /** Breiten der Reiter — Laenge = Zahl der Reiter */
  tabW?: number[];
  /** Diagrammraster: je Kachel Spannweite, Hoehe und Hinweiszeile */
  kacheln?: KachelMass[];
  /** Zeitstempel der Messung (Tage-Verfall) */
  t: number;
}

/** Felder, die nur in dem Reiter messbar sind, der gerade offen steht.
 *  Sie duerfen beim Schreiben NICHT genullt werden, bloss weil der andere
 *  Reiter oben liegt — sonst loeschte jeder Besuch der Objektliste das
 *  Wissen ueber das Diagrammraster. */
const NUR_WENN_SICHTBAR = ["kacheln"] as const;

/** Breitenbaender — dieselben Grenzen, an denen das Layout wirklich umspringt.
 *  340, 400 und 760 kamen mit dem Kachel-Mosaik dazu (base.css, .dash-grid): unter
 *  400 px steht eine Spalte, bis 760 px zwei, darueber drei. Ohne die beiden
 *  Grenzen teilten sich ein 390- und ein 420-px-Telefon EIN Band und damit
 *  EINE gespeicherte Messung — das Skelett zeichnete dann die Hoehe des
 *  jeweils anderen Layouts und die Seite sprang beim Umschalten. */
const BREITEN = [340, 400, 430, 560, 580, 760, 768, 880, Infinity] as const;
export function band(w?: number): number {
  const b = w ?? (typeof window === "undefined" ? 1440 : window.innerWidth);
  return BREITEN.findIndex((g) => b <= g);
}

/* Praefix skm4 (zuvor skm3, davor skm2): mit den zwei neuen Breitenbaendern verschieben sich ALLE
   Bandnummern — eine alte Messung laege sonst unter einer fremden Nummer und
   waere schlimmer als keine. Wie beim Sprung von skm1 auf skm2 laesst ein
   neuer Praefix die alten Eintraege einfach verfallen. */
export function schluessel(projektId: string, filterAktiv: boolean, rail: boolean, breite?: number): string {
  return `skm4.${projektId}.b${band(breite)}.f${filterAktiv ? 1 : 0}.r${rail ? 1 : 0}`;
}

const MAX_ALTER = 60 * 24 * 3600 * 1000; // 60 Tage

export function leseMass(key: string): SkelettMass | null {
  const m = load<SkelettMass | null>(key, null);
  if (!m || typeof m.t !== "number" || Date.now() - m.t > MAX_ALTER) return null;
  // Plausibilitaet: eine Messung mitten im Aufbau liefert Unsinn
  if (m.kopf < 20 || m.kopf > 240 || m.hero < 40 || m.hero > 220 || m.filter < 30 || m.filter > 260) return null;
  return m;
}

export function schreibeMass(key: string, m: Omit<SkelettMass, "t">): void {
  const alt = load<SkelettMass | null>(key, null);
  /* Zusammenfuehren statt ersetzen: was in DIESEM Durchgang nicht messbar war
     (der geschlossene Reiter), bleibt aus dem vorigen stehen. Ein `undefined`
     ist "nicht gesehen", nicht "gibt es nicht". */
  const neu: Omit<SkelettMass, "t"> = { ...m };
  if (alt)
    for (const k of NUR_WENN_SICHTBAR)
      if (neu[k] === undefined && alt[k] !== undefined) (neu as Record<string, unknown>)[k] = alt[k];
  /* Vergleich ueber ALLE Felder, nicht nur die sechs Hoehen. Frueher verglich
     diese Stelle bloss die Hoehen — mit dem Ergebnis, dass ein vorhandener
     Eintrag jede neue Erkenntnis abwies, solange die Hoehen gleich blieben.
     Genau daran waeren die Formfelder nie in den Speicher gekommen. */
  if (alt && vergleichbar(alt) === vergleichbar(neu)) return;
  save(key, { ...neu, t: Date.now() });
}

/** Alles ausser dem Zeitstempel, in stabiler Feldreihenfolge. */
function vergleichbar(m: Partial<SkelettMass>): string {
  const felder: Array<keyof SkelettMass> = ["rail", "kopf", "suche", "filter", "hero", "tabs", "titel", "kopfBtn", "chips", "chips2", "heroLab", "heroVal", "tabW", "kacheln"];
  return JSON.stringify(felder.map((k) => m[k] ?? null));
}

/** Breite des TEXTES, nicht des Kastens. Ein `flex: 1`-Kind ist so breit wie
 *  sein Platz; das Skelett zeichnet aber die Schrift nach, die dort steht.
 *  Auf die Kastenbreite gedeckelt, weil ein abgeschnittener Text (Ellipse)
 *  weiterhin seine volle Laenge meldet. */
function textBreite(el: Element | null | undefined): number {
  if (!el) return 0;
  const r = document.createRange();
  r.selectNodeContents(el);
  const t = r.getBoundingClientRect().width;
  const k = el.getBoundingClientRect().width;
  return Math.round(Math.min(t || k, k || t));
}

/**
 * Die fertige Seite vermessen. Liest ueber `data-skl`-Anker und ueber die
 * ECHTEN Klassennamen der Seite, nicht ueber Kindindexe: ein Refactor der
 * Reihenfolge macht die Messung dann nicht still falsch, sondern liefert
 * null bzw. undefined (und der Rueckfall greift).
 */
export function vermessen(wurzel: HTMLElement): Omit<SkelettMass, "t"> | null {
  /* Gemessen wird die EIGENE Hoehe jedes Blocks (border-box) — nicht Hoehe plus
     Aussenabstand.
     Der Umweg ueber den Abstand war zweimal falsch: der Filterblock ist ein
     Behaelter ohne Polster und Rand, der Aussenabstand seiner Karte faellt
     daran vorbei nach aussen durch (Randkollaps). getComputedStyle meldete
     dort brav "0px" — das Skelett kam 14 px zu hoch heraus, ab den Kacheln lief
     alles nach oben. Und beim Kachelblock steckt zwischen ihm und dem naechsten
     Anker noch der Rand und das Polster der Panel-Karte; ein gemessener
     "Abstand" haette die doppelt gezaehlt.
     Die Abstaende zwischen den Bloecken stehen im Skelett ohnehin — es baut
     dieselbe Abfolge nach. Gemessen werden muss nur, was sich mit Inhalt,
     Geraetebreite und Filterzustand aendert. */
  const anker = (name: string): HTMLElement | null => wurzel.querySelector<HTMLElement>(`[data-skl="${name}"]`);
  const hoehe = (name: string): number | null => {
    const el = anker(name);
    if (!el) return null;
    const r = el.getBoundingClientRect();
    // unsichtbar (display:none) => kein Mass, aber auch kein Fehler
    if (r.width === 0 && r.height === 0) return 0;
    return Math.round(r.height);
  };
  const kopf = hoehe("kopf");
  const suche = hoehe("suche");
  const filter = hoehe("filter");
  const hero = hoehe("hero");
  const tabs = hoehe("tabs");
  const rail = hoehe("rail") ?? 0;
  if (kopf == null || suche == null || filter == null || hero == null || tabs == null) return null;
  if (!kopf || !suche || !filter || !hero || !tabs) return null; // noch im Aufbau

  /** Kastenbreiten aller Treffer; leere Liste => undefined (nicht gesehen). */
  const breiten = (sel: string): number[] | undefined => {
    const w = [...wurzel.querySelectorAll<HTMLElement>(sel)].map((e) => Math.round(e.getBoundingClientRect().width)).filter((x) => x > 0);
    return w.length ? w : undefined;
  };
  /** Textbreiten aller Treffer. */
  const texte = (sel: string): number[] | undefined => {
    const w = [...wurzel.querySelectorAll<HTMLElement>(sel)].map((e) => textBreite(e)).filter((x) => x > 0);
    return w.length ? w : undefined;
  };

  const titel = textBreite(wurzel.querySelector('[data-skl="kopf"] h1')) || undefined;
  const kopfBtn = breiten('[data-skl="kopf"] button');
  const chips = breiten('[data-skl="filter"] .filterbar-chips:not(.filterbar-chips-2) > *');
  const chips2 = breiten('[data-skl="filter"] .filterbar-chips-2 > *');
  const heroLab = texte('[data-skl="hero"] .hero-label');
  const heroVal = texte('[data-skl="hero"] .hero-value');
  const tabW = breiten('[data-skl="tabs"] button');

  /* Diagrammraster. Steht der Reiter „Objektliste" oben, ist das Raster
     display:none — dann ALLE Rechtecke 0, und wir melden „nicht gesehen"
     statt „keine Kacheln". schreibeMass traegt in dem Fall den alten Stand
     weiter. */
  /* ERSTES Raster MIT Kacheln, nicht erstes Raster ueberhaupt: waehrend der
     Kreuzblende liegt die Silhouette des Diagrammblocks im selben Baum, und
     die baut inzwischen selbst ein .dash-grid (nur eben ohne data-tile).
     Sonst maesse man das Skelett und legte dessen Form als „Endmass" ab. */
  let kacheln: KachelMass[] | undefined;
  const raster = [...wurzel.querySelectorAll<HTMLElement>(".dash-grid")].find((g) => g.querySelector(":scope > [data-tile]"));
  if (raster && raster.getBoundingClientRect().height > 0) {
    const k = [...raster.querySelectorAll<HTMLElement>(":scope > [data-tile]")].map((el): KachelMass => {
      const r = el.getBoundingClientRect();
      // Die Hinweiszeile unter dem Diagramm ist das letzte Kind — ohne sie ist
      // die Kachel kuerzer, und im Raster entscheidet die hoechste Kachel ueber
      // die Zeilenhoehe. Also je Kachel merken, nicht ueber alle mitteln.
      const p = el.querySelector<HTMLElement>(":scope > p");
      const ph = p ? Math.round(p.getBoundingClientRect().height) : 0;
      return [Number(el.getAttribute("data-span")) || 1, Math.round(r.height), ph];
    });
    if (k.length) kacheln = k;
  }

  return { rail, kopf, suche, filter, hero, tabs, titel, kopfBtn, chips, chips2, heroLab, heroVal, tabW, kacheln };
}
